import React from 'react';
import SubTitle from './SubTitle';
import { typography } from '@/design-system/tokens/typography';

interface CounterButtonProps {
  value: number;
  onChange: (newValue: number) => void;
  label: string;
  description?: string;
  className?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
}

const CounterButton: React.FC<CounterButtonProps> = ({
  value,
  onChange,
  label,
  description,
  className,
  disabled = false,
  min = 0,
  max,
}) => {
  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && (max === undefined || value < max);
  return (
    <div className={`flex items-center justify-between mb-2 ${className}`}>
      <div>
        <SubTitle title={label} className='leading-[1.875rem]' />
        {description && (
          <p
            className='leading-4 font-light'
            style={{
              fontSize: typography.size.xs, // 12px
              fontFamily: typography.fonts.primary,
            }}
          >
            {description}
          </p>
        )}
      </div>
      <div className='flex items-center space-x-4'>
        {/* Decrement Button */}
        <button
          type='button'
          disabled={!canDecrement}
          className='w-10 h-10 border rounded-sm text-lg bg-secondary flex items-center justify-center focus-visible:outline-none active:scale-100 transition-none disabled:opacity-50 disabled:cursor-not-allowed'
          onClick={() =>
            canDecrement && onChange(Math.max(min, value - 1))
          }
        >
          -
        </button>

        {/* Current Value */}
        <span
          className='font-medium min-w-[1.5rem] text-center'
          style={{
            fontSize: typography.size.sm, // 14px
            fontFamily: typography.fonts.primary,
          }}
        >
          {value}
        </span>

        {/* Increment Button */}
        <button
          type='button'
          disabled={!canIncrement}
          className='w-10 h-10 border rounded-sm bg-secondary text-lg flex items-center justify-center focus-visible:outline-none active:scale-100 transition-none disabled:opacity-50 disabled:cursor-not-allowed'
          onClick={() =>
            canIncrement &&
            onChange(max === undefined ? value + 1 : Math.min(max, value + 1))
          }
        >
          +
        </button>
      </div>
    </div>
  );
};

export default CounterButton;
